import React, { useState, useEffect } from "react";
import { Routes, Route, Navigate, useNavigate, useLocation } from "react-router-dom";
import { Container, Box } from '@mantine/core';
import PlatformNavBar from '../../components/PlatformNavBar';

// Import Five9 components
import MainLayout from "./components/MainLayout";
import SpreadsheetProcessor from "./SpreadsheetProcessor";
import LandingPage from "./LandingPage";
import DDDImport from "./DDDLandingPage";
import NewProject from "./NewProject";
import ReturningProject from "./ReturningProject";
import ToolsLanding from "./ToolsLandingPage";
import Tools from "./Tools";
import ContextServices from "./tools/ContextServices/ContextServices";
import FedProcessor from "./tools/FederationIDImport/FederationIDImport";
import SpeedDialProcessor from "./tools/SpeedDialImport/SpeedDialImport";
import AutoAnswerProcessor from "./tools/AutoAnswer/AutoAnswerImport";
import PasswordUpdateProcessor from "./tools/PasswordUpdate/PasswordUpdate";

// Import Footer component
import Footer from '../../components/Footer';

function Five9App({ onBackToPlatformSelector, platformInfo }) {
  const [soapCredentials, setSoapCredentials] = useState(null);
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [redirectPath, setRedirect] = useState(null);
  const navigate = useNavigate();
  const location = useLocation();

  // Debug logs to track credential flow
  useEffect(() => {
    console.log("🔐 Credentials updated:", soapCredentials);
    console.log("🔓 Authentication status:", isAuthenticated);
    console.log("📍 Redirect path:", redirectPath);
  }, [soapCredentials, isAuthenticated, redirectPath]);

  // === PROJECT HANDLERS ===
  const projectHandlers = {
    handleReturningProject: (formData) => {
      console.log("📊 Returning Project Handler - Received:", formData);
      
      // Handle both direct credentials and nested credentials
      const credentials = formData.credentials ? formData.credentials : formData;
      
      if (!credentials.username || !credentials.password) {
        console.error("❌ Missing credentials:", credentials);
        alert("Missing username or password");
        return;
      }
      
      console.log("✅ Setting credentials for returning project:", credentials);
      setSoapCredentials(credentials);
      setIsAuthenticated(true);
      setRedirect("/spreadsheet");
    },

    handleNewProject: (formData) => {
      console.log("🆕 New Project Handler - Received:", formData);
      
      const credentials = formData.credentials ? formData.credentials : formData;
      
      if (!credentials.username || !credentials.password) {
        console.error("❌ Missing credentials:", credentials);
        alert("Missing username or password");
        return;
      }
      
      console.log("✅ Setting credentials for new project:", credentials);
      setSoapCredentials(credentials);
      setIsAuthenticated(true);
      setRedirect("/spreadsheet");
    },

    handleDDDImport: (formData) => {
      console.log("📥 DDD Import Handler - Received:", formData);
      
      const credentials = formData.credentials ? formData.credentials : formData;
      
      if (!credentials.username || !credentials.password) {
        console.error("❌ Missing credentials:", credentials);
        alert("Missing username or password");
        return;
      }
      
      console.log("✅ Setting credentials for DDD import:", credentials);
      setSoapCredentials(credentials);
      setIsAuthenticated(true);
      setRedirect("/spreadsheet");
    }
  };

  // === TOOLS HANDLERS ===
  const toolsHandlers = {
    handleToolsLanding: (formData) => {
      console.log("🔧 Tools Landing Handler - Received:", formData);
      
      const credentials = formData.credentials ? formData.credentials : formData;
      
      if (!credentials.username || !credentials.password) {
        console.error("❌ Missing credentials:", credentials);
        alert("Missing username or password");
        return;
      }
      
      console.log("✅ Setting credentials for tools:", credentials);
      setSoapCredentials(credentials);
      setIsAuthenticated(true);
      setRedirect("/toolslist");
    },

    handleToolImport: (formData, redirectPath) => {
      console.log("⚙️ Tool Import Handler - Received:", formData, "Redirect:", redirectPath);
      
      // If credentials are already set and we're just switching tools, maintain them
      if (soapCredentials && isAuthenticated) {
        console.log("✅ Using existing credentials for tool switch");
        setRedirect(redirectPath);
        return;
      }
      
      const credentials = formData.credentials ? formData.credentials : formData;
      
      if (!credentials.username || !credentials.password) {
        console.error("❌ Missing credentials:", credentials);
        alert("Missing username or password");
        return;
      }
      
      console.log("✅ Setting credentials for tool import:", credentials);
      setSoapCredentials(credentials);
      setIsAuthenticated(true);
      setRedirect(redirectPath);
    }
  };

  // === AUTH HANDLERS ===
  const authHandlers = {
    handleBack: () => {
      console.log("🔙 Back button pressed - clearing session");
      setSoapCredentials(null);
      setIsAuthenticated(false);
      setRedirect(null);
    },

    handleLogout: () => {
      console.log("🚪 Logout - clearing session");
      setSoapCredentials(null);
      setIsAuthenticated(false);
      setRedirect("/");
    }
  };

  // Clear redirect after navigation
  useEffect(() => {
    if (redirectPath) {
      console.log("🚀 Navigating to:", redirectPath);
      const timer = setTimeout(() => {
        console.log("🧹 Clearing redirect path");
        setRedirect(null);
      }, 100);
      return () => clearTimeout(timer);
    }
  }, [redirectPath]);

  return (
    <Box style={{ display: 'flex', flexDirection: 'column', minHeight: '100vh' }}>
      <Container size="xl" style={{ flex: 1, padding: '20px' }}>
        {/* Top-left navigation: exit to platform selector + return to Five9 home.
            The home button is pure navigation to '/' (LandingPage); MainLayout's
            bottom "Back" remains the credential-clearing exit for auth flows. */}
        <PlatformNavBar
          onBackToPlatformSelector={onBackToPlatformSelector}
          onBackToHome={() => navigate('/')}
          showHome={location.pathname !== '/'}
          homeLabel="Five9 home"
          color="green"
          mb="xl"
        />

        {/* Debug info in development */}
        {process.env.NODE_ENV === 'development' && (
          <div style={{ position: 'fixed', top: 0, right: 0, background: 'rgba(0,0,0,0.8)', color: 'white', padding: '10px', fontSize: '12px', zIndex: 9999 }}>
            <div>Auth: {isAuthenticated ? '✅' : '❌'}</div>
            <div>User: {soapCredentials?.username || 'None'}</div>
            <div>Redirect: {redirectPath || 'None'}</div>
          </div>
        )}

        {/* Redirect if a path is set */}
        {redirectPath && <Navigate to={redirectPath} replace />}

        <MainLayout
          onBack={authHandlers.handleBack}
          isAuthenticated={isAuthenticated}
          username={soapCredentials?.username}
        >
          <Routes>
          {/* Landing and Main Routes */}
          <Route path="/" element={<LandingPage />} />
          
          {/* Project Routes */}
          <Route 
            path="/ddd-import" 
            element={<DDDImport onSubmit={projectHandlers.handleDDDImport} />} 
          />
          <Route 
            path="/new-project" 
            element={<NewProject onSubmit={projectHandlers.handleNewProject} />} 
          />
          <Route 
            path="/returning-project" 
            element={<ReturningProject onSubmit={projectHandlers.handleReturningProject} />} 
          />
          <Route 
            path="/spreadsheet" 
            element={
              soapCredentials ? (
                <SpreadsheetProcessor soapCredentials={soapCredentials} />
              ) : (
                <Navigate to="/returning-project" replace />
              )
            } 
          />
          
          {/* Tools Routes */}
          <Route 
            path="/tools" 
            element={<ToolsLanding onSubmit={toolsHandlers.handleToolsLanding} />} 
          />
          <Route 
            path="/toolslist" 
            element={
              soapCredentials ? (
                <Tools 
                  soapCredentials={soapCredentials} 
                  onSubmit={toolsHandlers.handleToolImport}
                />
              ) : (
                <Navigate to="/tools" replace />
              )
            } 
          />
          
          {/* Individual Tool Routes */}
          <Route 
            path="/contextservices" 
            element={
              soapCredentials ? (
                <ContextServices soapCredentials={soapCredentials} />
              ) : (
                <Navigate to="/tools" replace />
              )
            } 
          />
          <Route 
            path="/fedimport" 
            element={
              soapCredentials ? (
                <FedProcessor soapCredentials={soapCredentials} />
              ) : (
                <Navigate to="/tools" replace />
              )
            } 
          />
          <Route 
            path="/passwordupdate" 
            element={
              soapCredentials ? (
                <PasswordUpdateProcessor soapCredentials={soapCredentials} />
              ) : (
                <Navigate to="/tools" replace />
              )
            } 
          />
          <Route 
            path="/speedimport" 
            element={
              soapCredentials ? (
                <SpeedDialProcessor soapCredentials={soapCredentials} />
              ) : (
                <Navigate to="/tools" replace />
              )
            } 
          />
          <Route 
            path="/autoanswer" 
            element={
              soapCredentials ? (
                <AutoAnswerProcessor soapCredentials={soapCredentials} />
              ) : (
                <Navigate to="/tools" replace />
              )
            } 
          />
        </Routes>
      </MainLayout>
    </Container>

    {/* Footer with Five9 green theme */}
    <Footer platformColor="green" />
  </Box>
  );
}

export default Five9App;